/**
 * SLA policies — Step 31.
 *
 * A policy MATCHES a ticket; its targets say WHAT IS PROMISED per priority.
 *
 * Two rules the UI has to make obvious, because getting either wrong is
 * expensive and silent:
 *
 * 1. **First match wins, top to bottom.** Order is the only conflict
 *    resolution, and it is the only one anyone finds predictable. So the list
 *    is ordered and the order is visibly editable.
 * 2. **The default policy is last and cannot be deleted.** A ticket matching
 *    no policy would get no clock and vanish from every breach report — the
 *    failure nobody notices until a customer does.
 *
 * Targets are entered in HOURS and stored in business milliseconds. Hours are
 * what people say ("four hour response"); milliseconds are what the maths
 * needs. Converting at the edge keeps both honest.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Plus, Timer, Trash2 } from 'lucide-react';
import { apiFetch, apiSend } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { TICKET_SOURCES } from '@itsm/shared';
import { ConditionBuilder, type ConditionField } from '../../components/ConditionBuilder';
import type { Condition } from '@itsm/shared';
import { useConfirm } from '../../lib/confirm';
import { toast } from 'sonner';

const HOUR = 3_600_000;
const PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;

interface Target {
  priority: string;
  firstResponseMs: number | null;
  resolutionMs: number | null;
  /** 1 = 24/7 for THIS priority, ignoring the policy calendar. */
  useCalendarHours?: number;
  escalationEnabled?: number;
  escalateBeforeMs: number | null;
  escalateGroupId?: string | null;
}
/** One `field = value` clause. The stored grammar is the SHARED one
 *  (`packages/shared/conditions.ts`) — its keys are `fieldKey`/`op`/`value`,
 *  and getting that wrong means a policy silently matches nothing. */
/** Operators exposed in the editor. The shared grammar has more
 *  (contains/isEmpty/…); these are the three the reference offers on an SLA
 *  policy — "is", "is not", "includes" — and the three that read as English. */
type ClauseOp = 'eq' | 'neq' | 'in';
interface Clause {
  op: ClauseOp;
  fieldKey: string;
  /** `in` carries a list; eq/neq a single value. Kept as one field so the
   *  editor does not need two shapes. */
  value: string | string[];
}
const OPS: { op: ClauseOp; label: string }[] = [
  { op: 'eq', label: 'is' },
  { op: 'neq', label: 'is not' },
  { op: 'in', label: 'is any of' },
];
type AndNode = { op: 'and'; conditions: Clause[] };
type OrNode = { op: 'or'; conditions: (Clause | AndNode)[] };
type Conditions = OrNode | AndNode | Clause | null;

interface Policy {
  id: string;
  name: string;
  description: string | null;
  calendarId: string | null;
  conditions: Conditions;
  position: number;
  isDefault: number;
  isActive: number;
  targets: Target[];
}

/** The ticket facts `slaEngine.resolvePolicy` puts in scope. Deliberately a
 *  small, fixed list: a condition naming anything else evaluates to false and
 *  the policy silently never matches. */
const MATCH_FIELDS: ConditionField[] = [
  {
    key: 'type',
    label: 'Type',
    options: [
      { value: 'incident', label: 'Incident' },
      { value: 'service_request', label: 'Service request' },
    ],
  },
  {
    key: 'priority',
    label: 'Priority',
    options: PRIORITIES.map((p) => ({ value: p, label: p[0]!.toUpperCase() + p.slice(1) })),
  },
  { key: 'source', label: 'Source', options: TICKET_SOURCES.map((x) => ({ value: x.key, label: x.label })) },
  { key: 'groupId', label: 'Group' },
  // The reference matches on "Ticket Fields.Agent" too — a policy like
  // "anything assigned to the on-call agent" is a real ask.
  { key: 'assigneeId', label: 'Agent' },
];

/**
 * The editor's shape: GROUPS of clauses. Within a group every clause must
 * match (AND); a ticket matches the policy if ANY group matches (OR).
 *
 * That expresses `(A and B) or (C and D)` — Kai: *"I should be able to set AND
 * this AND that OR THIS or THAT type policy"* — without exposing arbitrary
 * nesting, which is a tree editor nobody can read at a glance. Two levels is
 * the most power that still renders as a sentence.
 *
 * Maps onto the SHARED grammar (`packages/shared/conditions.ts`) as
 * `{op:'or', conditions:[{op:'and', conditions:[…]}, …]}`, collapsing the
 * redundant wrappers when there is only one group or one clause.
 */
function toGroups(c: Conditions): Clause[][] {
  if (!c) return [];
  if ('fieldKey' in c) return [[c]];
  if (c.op === 'and') return [c.conditions.filter((x) => 'fieldKey' in x) as Clause[]];
  // 'or' — each member is either a single clause or an AND group.
  return c.conditions.map((m) =>
    'fieldKey' in m ? [m] : ((m as { conditions: Clause[] }).conditions ?? []),
  );
}

/** A readable sentence for the list — a rules engine whose rules you cannot
 *  read at a glance is one nobody trusts. */
function describe(
  c: Conditions,
  fieldLabel: (k: string) => string,
  valueLabel: (k: string, v: string) => string,
): string {
  const groups = toGroups(c);
  if (groups.length === 0) return 'Every ticket';
  return groups
    .map((g) =>
      g
        .map((x) => {
          const opLabel = OPS.find((o) => o.op === x.op)?.label ?? 'is';
          const v = Array.isArray(x.value)
            ? x.value.map((vv) => valueLabel(x.fieldKey, vv)).join(' or ')
            : valueLabel(x.fieldKey, x.value);
          return `${fieldLabel(x.fieldKey)} ${opLabel} ${v}`;
        })
        .join(' and '),
    )
    .map((t, _, arr) => (arr.length > 1 ? `(${t})` : t))
    .join(' OR ');
}

interface Calendar {
  id: string;
  name: string;
  isDefault: number;
}

const api = {
  list: () => apiFetch<{ entries: Policy[] }>('/api/admin/sla-policies'),
  calendars: () => apiFetch<{ entries: Calendar[] }>('/api/admin/business-calendars'),
  create: (body: unknown) => apiSend<{ id: string }>('/api/admin/sla-policies', 'POST', body),
  update: (id: string, body: unknown) => apiSend(`/api/admin/sla-policies/${id}`, 'PATCH', body),
  remove: (id: string) => apiSend(`/api/admin/sla-policies/${id}`, 'DELETE'),
};

/**
 * Durations are entered as a NUMBER plus a UNIT, not as hours.
 *
 * The reference offers Mins · Hrs · Days · Mos, and constrains the whole range
 * to "within 15 min to 12 months". Hours-only forced "30 minutes" to be typed
 * as 0.5, which is how a 30-second promise gets entered by accident.
 */
const UNITS = [
  { key: 'm', label: 'mins', ms: 60_000 },
  { key: 'h', label: 'hrs', ms: HOUR },
  { key: 'd', label: 'days', ms: 24 * HOUR },
] as const;
type UnitKey = (typeof UNITS)[number]['key'];

/** Pick the largest unit that divides cleanly, so 4h reads "4 hrs" not "240 mins". */
function splitDuration(ms: number | null): { n: string; unit: UnitKey } {
  if (ms == null) return { n: '', unit: 'h' };
  for (const u of [...UNITS].reverse()) {
    if (ms % u.ms === 0) return { n: String(ms / u.ms), unit: u.key };
  }
  return { n: String(Math.round(ms / 60_000)), unit: 'm' };
}
function joinDuration(n: string, unit: UnitKey): number | null {
  if (n.trim() === '') return null;
  const u = UNITS.find((x) => x.key === unit)!;
  return Math.round(Number(n) * u.ms);
}
/** The reference's own floor and ceiling, adopted verbatim. */
const MIN_MS = 15 * 60_000;
const MAX_MS = 365 * 24 * HOUR;

export function SlaPoliciesPage() {
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: qk.slaPolicies,
    queryFn: api.list,
  });
  const { data: cals } = useQuery({ queryKey: qk.businessCalendars, queryFn: api.calendars });
  // For the "Group is …" condition. Reuses the admin groups endpoint rather
  // than a second list of the same thing.
  const { data: groupList } = useQuery({
    queryKey: qk.adminGroups,
    queryFn: () => apiFetch<{ entries: { id: string; name: string }[] }>('/api/admin/groups'),
  });
  // WHICH policy is open belongs in the URL (`?edit=<id>` / `?edit=new`) —
  // a refresh keeps your place and the link is shareable. The draft itself
  // stays local: an unsaved edit is not a location.
  const [editId, setEditId] = useUrlState('edit', null);
  const [editing, setEditing] = useState<Policy | null>(null);
  // The condition rows being edited, held as their OWN state.
  //
  // The bug this fixes: they were derived from `editing.conditions` on every
  // render, and `fromClauses` drops a clause whose value is empty — so
  // choosing a field (which clears the value) made the row delete itself
  // mid-edit. An in-progress row is not a finished condition, and the two
  // must not share a representation.
  // The grammar itself, not a UI-shaped projection of it: `toGroups` /
  // `fromGroups` existed only to feed the hand-rolled editor.
  const [conditions, setConditions] = useState<Condition | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.slaPolicies });

  /** Open a policy for editing — seeds the clause rows from stored conditions. */
  const open = (pol: Policy) => {
    setEditing(pol);
    setConditions(pol.conditions as Condition | undefined);
    setEditId(pol.id || 'new');
    setFormError(null);
  };
  // Clear the guard FIRST: `editId` lives in the URL, so closing the editor
  // is a NAVIGATION, and the unsaved-changes blocker would otherwise fire on
  // Save (nothing unsaved) and on Cancel (discarding is what was asked for).
  const close = () => {
    clearDirty();
    setEditing(null);
    setConditions(undefined);
    setEditId(null);
    setFormError(null);
  };

  const save = useMutation({
    mutationFn: (p: Policy) => {
      const body = {
        name: p.name,
        description: p.description ?? undefined,
        calendarId: p.calendarId,
        // The API's own type is this page's narrower `Conditions`; the
        // grammar is the wider truth, so the cast is at the boundary.
        conditions: (conditions ?? { op: 'and', conditions: [] }) as Conditions,
        position: p.position,
        isActive: !!p.isActive,
        targets: p.targets.map((t) => ({
          priority: t.priority,
          firstResponseMs: t.firstResponseMs,
          resolutionMs: t.resolutionMs,
          escalateBeforeMs: t.escalateBeforeMs,
        })),
      };
      return p.id ? api.update(p.id, body) : api.create(body);
    },
    onSuccess: (res) => {
      if (!res.ok) {
        setFormError(res.error ?? 'Could not save that policy');
        return;
      }
      close();
      invalidate();
    },
  });

  const move = async (p: Policy, delta: number) => {
    await api.update(p.id, { position: Math.max(0, p.position + delta) });
    invalidate();
  };

  // Hooks run on EVERY render, so these sit ABOVE the loading/error returns.
  // Placing them after an early return is React error #310 — which is exactly
  // what the first attempt at this page did.
  const entries: Policy[] = data?.entries ?? [];

  /**
   * Re-open the editor from the URL on a refresh or a deep link. Seeding in
   * an effect (not at render) means a slow list cannot clobber an edit that
   * is already in progress.
   */
  useEffect(() => {
    if (!editId) return;
    if (editing) return; // already open — never re-seed over live typing
    if (editId === 'new') return; // a brand-new policy has nothing to restore
    const found = entries.find((x) => x.id === editId);
    if (found) {
      setEditing(found);
      setConditions(found.conditions as Condition | undefined);
    }
  }, [editId, entries, editing]);

  /**
   * Dirty against the SERVER's copy, so Save lights up only for a real
   * change. Kai: *"it shows active always"* — a Save button that is enabled
   * on an untouched form is a lie about state, and people stop trusting it.
   *
   * Conditions are compared through the serialiser because the editor holds
   * them as groups while the server holds a condition tree.
   */
  const serverPolicy = editId && editId !== 'new' ? entries.find((x) => x.id === editId) : null;
  const dirty =
    !!editing &&
    (editId === 'new' ||
      !serverPolicy ||
      JSON.stringify({
        name: editing.name,
        description: editing.description,
        calendarId: editing.calendarId,
        isActive: editing.isActive,
        // The API's own type is this page's narrower `Conditions`; the
        // grammar is the wider truth, so the cast is at the boundary.
        conditions: (conditions ?? { op: 'and', conditions: [] }) as Conditions,
        targets: editing.targets,
      }) !==
        JSON.stringify({
          name: serverPolicy.name,
          description: serverPolicy.description,
          calendarId: serverPolicy.calendarId,
          isActive: serverPolicy.isActive,
          conditions: serverPolicy.conditions,
          targets: serverPolicy.targets,
        }));
  const clearDirty = useDirty(dirty);

  if (isPending) return <Skeleton className="m-6 h-64 w-full max-w-4xl" />;
  if (isError)
    return (
      <div className="m-6 max-w-3xl space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">Could not load SLA policies</p>
        <p className="text-xs text-destructive/80">{(error as Error).message}</p>
        <button
          onClick={() => refetch()}
          className="text-xs text-destructive/80 underline hover:text-destructive"
        >
          Retry
        </button>
      </div>
    );

  const p = editing;

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-1 flex items-center gap-3">
        <Timer className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">SLA policies</h1>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() =>
            open({
              id: '',
              name: '',
              description: null,
              calendarId: cals?.entries.find((c) => c.isDefault)?.id ?? null,
              conditions: { op: 'eq' as ClauseOp, fieldKey: 'type', value: 'incident' },
              position: (data!.entries.filter((x) => !x.isDefault).length || 0) + 1,
              isDefault: 0,
              isActive: 1,
              targets: PRIORITIES.map((priority) => ({
                priority,
                firstResponseMs: 4 * HOUR,
                resolutionMs: 24 * HOUR,
                escalateBeforeMs: null,
              })),
            })
          }
        >
          <Plus /> New policy
        </Button>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">
        The first policy whose conditions match wins, top to bottom. The default policy sits last and catches
        everything else, so every ticket gets a promise.
      </p>

      {p ? (
        <div className="space-y-5 rounded-lg border bg-card p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium">Name</label>
              <Input
                autoFocus
                value={p.name}
                onChange={(e) => setEditing({ ...p, name: e.target.value })}
                placeholder="Urgent incidents"
                disabled={!!p.isDefault}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Business hours</label>
              <Select
                value={p.calendarId ?? ''}
                onChange={(e) => setEditing({ ...p, calendarId: e.target.value || null })}
              >
                <option value="">24/7 (no calendar)</option>
                {(cals?.entries ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Applies to — the missing half. Without this a policy has no way to
              say "incidents only", and (worse) the engine treats a
              condition-less non-default policy as matching EVERYTHING, so it
              silently shadows every policy below it including the catch-all. */}
          <div>
            <label className="mb-1 block text-sm font-medium">Applies to</label>
            <p className="mb-2 text-xs text-muted-foreground">
              All of these must match. Leave empty to apply to every ticket — but then this policy shadows
              everything below it, so put it last.
            </p>
            <div className="space-y-2">
              {/* THE shared condition builder — Step 42's mandate. This page
                  had the most capable of the three copies (it already had
                  groups and multi-select), and the others had drifted behind
                  it one fix at a time. So the shared control was made the
                  UNION of the copies rather than the intersection: nesting
                  came from here, `contains` and the missing operators from the
                  grammar, and the whole thing now has one implementation.

                  `conditions` is stored as the grammar, so nothing about the
                  saved shape changes — `toGroups`/`fromGroups` existed only to
                  feed the old UI. */}
              <ConditionBuilder
                value={conditions}
                onChange={setConditions}
                fields={MATCH_FIELDS}
                defaultFieldKey="priority"
                addLabel="Add condition"
              />
              {!conditions && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  With no conditions this policy matches every ticket, so it shadows everything below it.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium">Targets</label>
            <p className="mb-2 text-xs text-muted-foreground">
              In working hours, not wall-clock hours. Leave blank for “no promise at this priority”.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[46rem] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1.5 pr-3 font-medium">Priority</th>
                    <th className="py-1.5 pr-3 font-medium">Respond within</th>
                    <th className="py-1.5 pr-3 font-medium">Resolve within</th>
                    <th className="py-1.5 pr-3 font-medium">Operational hrs</th>
                    <th className="py-1.5 font-medium">Warn before</th>
                  </tr>
                </thead>
                <tbody>
                  {PRIORITIES.map((priority) => {
                    const t =
                      p.targets.find((x) => x.priority === priority) ??
                      ({
                        priority,
                        firstResponseMs: null,
                        resolutionMs: null,
                        escalateBeforeMs: null,
                      } as Target);
                    const set = (patch: Partial<Target>) =>
                      setEditing({
                        ...p,
                        targets: PRIORITIES.map((pr) => {
                          const cur =
                            p.targets.find((x) => x.priority === pr) ??
                            ({
                              priority: pr,
                              firstResponseMs: null,
                              resolutionMs: null,
                              escalateBeforeMs: null,
                            } as Target);
                          return pr === priority ? { ...cur, ...patch } : cur;
                        }),
                      });

                    /** A number + unit pair. Hours-only meant "30 minutes" had
                     *  to be typed as 0.5, which is how a wrong promise gets
                     *  entered by accident. */
                    const duration = (
                      ms: number | null,
                      onChange: (v: number | null) => void,
                      label: string,
                    ) => {
                      const { n, unit } = splitDuration(ms);
                      return (
                        <span className="flex items-center gap-1">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            className="h-8 w-16"
                            value={n}
                            aria-label={label}
                            onChange={(e) => onChange(joinDuration(e.target.value, unit))}
                          />
                          <Select
                            value={unit}
                            className="h-8 w-20"
                            aria-label={`${label} unit`}
                            onChange={(e) => onChange(joinDuration(n, e.target.value as UnitKey))}
                          >
                            {UNITS.map((u) => (
                              <option key={u.key} value={u.key}>
                                {u.label}
                              </option>
                            ))}
                          </Select>
                        </span>
                      );
                    };

                    const outOfRange = [t.firstResponseMs, t.resolutionMs].some(
                      (v) => v != null && (v < MIN_MS || v > MAX_MS),
                    );

                    return (
                      <tr key={priority} className="border-b last:border-0 align-top">
                        <td className="py-1.5 pr-3 capitalize">{priority}</td>
                        <td className="py-1.5 pr-3">
                          {duration(t.firstResponseMs, (v) => set({ firstResponseMs: v }), 'Respond within')}
                        </td>
                        <td className="py-1.5 pr-3">
                          {duration(t.resolutionMs, (v) => set({ resolutionMs: v }), 'Resolve within')}
                        </td>
                        <td className="py-1.5 pr-3">
                          {/* Per PRIORITY, not per policy — an urgent P1 burns
                              24/7 on a desk that answers routine work 9–5. */}
                          <Select
                            value={t.useCalendarHours ? 'calendar' : 'business'}
                            className="h-8 w-32"
                            aria-label={`Operational hours for ${priority}`}
                            onChange={(e) => set({ useCalendarHours: e.target.value === 'calendar' ? 1 : 0 })}
                          >
                            <option value="business">Business hrs</option>
                            <option value="calendar">Calendar (24/7)</option>
                          </Select>
                        </td>
                        <td className="py-1.5">
                          <div className="flex flex-col gap-1">
                            {duration(t.escalateBeforeMs, (v) => set({ escalateBeforeMs: v }), 'Warn before')}
                            <Select
                              value={t.escalateGroupId ?? ''}
                              className="h-8 w-40"
                              aria-label={`Escalate ${priority} to`}
                              onChange={(e) => set({ escalateGroupId: e.target.value || null })}
                            >
                              <option value="">Notify the ticket's group</option>
                              {(groupList?.entries ?? []).map((g) => (
                                <option key={g.id} value={g.id}>
                                  Notify {g.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                          {outOfRange && (
                            <p className="mt-1 text-xs text-amber-600 dark:text-amber-500">
                              Targets should be between 15 minutes and 12 months.
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Audit gap: the list rendered an "Off" badge that nothing could
              set. A policy you cannot switch off has to be deleted instead,
              which loses its targets. */}
          <label className="flex cursor-pointer items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!p.isActive}
              onChange={(e) => setEditing({ ...p, isActive: e.target.checked ? 1 : 0 })}
              className="h-3.5 w-3.5 rounded border-input"
            />
            Active — apply this policy to new tickets
          </label>

          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button disabled={!dirty || !p.name.trim() || save.isPending} onClick={() => save.mutate(p)}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {data!.entries.map((pol, i) => (
            <div key={pol.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-muted-foreground">{pol.isDefault ? '—' : i + 1}</span>
                <span className="text-sm font-medium">{pol.name}</span>
                {!!pol.isDefault && <Badge variant="outline">Catch-all</Badge>}
                {!pol.isActive && (
                  <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                    Off
                  </Badge>
                )}
                <div className="flex-1" />
                {!pol.isDefault && (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => move(pol, -1)}
                      aria-label={`Move ${pol.name} up`}
                    >
                      <ChevronUp />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => move(pol, 1)}
                      aria-label={`Move ${pol.name} down`}
                    >
                      <ChevronDown />
                    </Button>
                  </>
                )}
                <Button size="sm" variant="outline" onClick={() => open(pol)}>
                  Edit
                </Button>
                {!pol.isDefault && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-destructive/40 text-destructive hover:bg-destructive/10"
                    onClick={async () => {
                      if (
                        !(await confirm({
                          title: `Delete “${pol.name}”?`,
                          destructive: true,
                          confirmLabel: 'Delete',
                        }))
                      )
                        return;
                      const res = await api.remove(pol.id);
                      if (!res.ok) toast.error(res.error ?? 'Could not delete');
                      invalidate();
                    }}
                    aria-label={`Delete ${pol.name}`}
                  >
                    <Trash2 />
                  </Button>
                )}
              </div>
              {/* What it matches, as a readable sentence. A rules engine whose
                  rules you cannot read at a glance is one nobody trusts. */}
              <p className="mt-2 text-xs text-muted-foreground">
                {pol.isDefault
                  ? 'Every ticket no other policy matched'
                  : describe(
                      pol.conditions,
                      (k) => MATCH_FIELDS.find((x) => x.key === k)?.label ?? k,
                      (k, v) =>
                        MATCH_FIELDS.find((x) => x.key === k)?.options?.find((o) => o.value === v)?.label ??
                        (groupList?.entries ?? []).find((g) => g.id === v)?.name ??
                        v,
                    )}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {pol.targets.length
                  ? pol.targets
                      .filter((t) => t.firstResponseMs || t.resolutionMs)
                      .map(
                        (t) =>
                          `${t.priority}: ${t.firstResponseMs ? `${t.firstResponseMs / HOUR}h reply` : '—'}${
                            t.resolutionMs ? ` / ${t.resolutionMs / HOUR}h resolve` : ''
                          }`,
                      )
                      .join(' · ') || 'No targets set'
                  : 'No targets set'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
